import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ROLES, type Role, type SsoConnection, type SsoConnectionCreate } from "@backstory/protocol";
import { api, queryKeys } from "../../api";
import { authStore } from "../../auth/store";
import { OrgLayout } from "../../components/Shell";
import { CopyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, Switch, useToast } from "../../components/ui";
import { ROLE_LABELS, ROLE_SUMMARY } from "../../auth/roles";
import { SaveBar, settingsEqual, UnsavedGuard } from "../../components/UnsavedChanges";

/** Owner is never granted by an identity provider; the API refuses it as a default or mapped role. */
const SSO_ROLES = ROLES.filter((r) => r !== "owner");

const empty = (type: "saml" | "oidc"): SsoConnectionCreate => ({
  type,
  name: type === "saml" ? "Okta SAML" : "Entra ID (OIDC)",
  enabled: true,
  defaultRole: "developer",
  groupRoleMap: {},
  jitProvisioning: true,
});

const connectionForm = (c: SsoConnection): SsoConnectionCreate => ({
  type: c.type,
  name: c.name,
  enabled: c.enabled,
  idpEntityId: c.idpEntityId,
  idpSsoUrl: c.idpSsoUrl,
  idpCertificate: c.idpCertificate,
  issuer: c.issuer,
  clientId: c.clientId,
  defaultRole: c.defaultRole,
  groupRoleMap: c.groupRoleMap,
  jitProvisioning: c.jitProvisioning,
});

export function SsoPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const conns = useQuery({ queryKey: queryKeys.sso(orgId), queryFn: () => api.orgs.sso(orgId) });
  const org = useQuery({ queryKey: queryKeys.org(orgId), queryFn: () => api.orgs.get(orgId) });
  const members = useQuery({ queryKey: queryKeys.members(orgId), queryFn: () => api.orgs.members(orgId) });
  const [editing, setEditing] = useState<{ id?: string; form: SsoConnectionCreate; existing?: SsoConnection } | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [deleting, setDeleting] = useState<SsoConnection | null>(null);
  const [groupKey, setGroupKey] = useState("");
  const [groupRole, setGroupRole] = useState<Role>("developer");

  // Enforcement lives on org settings but belongs next to the connections it
  // depends on, so this page owns the draft and patches the org.
  const [requireSso, setRequireSso] = useState(false);
  const [breakGlass, setBreakGlass] = useState<string[]>([]);
  useEffect(() => {
    if (!org.data) return;
    setRequireSso(org.data.settings.requireSso);
    setBreakGlass(org.data.settings.breakGlassUserIds);
  }, [org.data]);

  const breakGlassEligible = (members.data ?? []).filter((m) => m.role === "owner" && m.mfaEnabled);
  const hasEnabledConnection = (conns.data ?? []).some((c) => c.enabled);
  const missingBreakGlass = requireSso && breakGlass.length === 0;
  const enforcementDirty = !!org.data && (requireSso !== org.data.settings.requireSso || breakGlass.join(",") !== org.data.settings.breakGlassUserIds.join(","));
  const editingBaseline = editing?.existing ? connectionForm(editing.existing) : editing ? empty(editing.form.type) : null;
  const connectionDirty = !!editing && !settingsEqual(editing.form, editingBaseline);

  const saveEnforcement = useMutation({
    mutationFn: () => api.orgs.patch(orgId, { settings: { requireSso, breakGlassUserIds: breakGlass } }),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.org(orgId), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.org(orgId) });
      void authStore.load();
      toast.push("success", "Sign-in policy saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const save = useMutation({
    mutationFn: () => (editing?.id ? api.orgs.patchSso(orgId, editing.id, editing.form) : api.orgs.createSso(orgId, editing!.form)),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: queryKeys.sso(orgId) });
      toast.push("success", "SSO connection saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.orgs.deleteSso(orgId, id),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: queryKeys.sso(orgId) });
      toast.push("info", "Connection deleted.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const f = editing?.form;
  const setF = (patch: Partial<SsoConnectionCreate>) => editing && setEditing({ ...editing, form: { ...editing.form, ...patch } });
  const closeEditing = () => {
    if (connectionDirty) {
      setDiscardOpen(true);
      return;
    }
    setEditing(null);
  };

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader
        title="Single sign-on"
        actions={
          <>
            <button className="btn" onClick={() => setEditing({ form: empty("oidc") })}>Add OIDC</button>
            <button className="btn primary" onClick={() => setEditing({ form: empty("saml") })}>Add SAML</button>
          </>
        }
      />
      <div className="card stack-table">
        <DataTable
          ariaLabel="SSO connections"
          rows={conns.data ?? []}
          loading={conns.isPending}
          rowKey={(c) => c.id}
          empty="No SSO connections. Members sign in with local accounts."
          columns={[
            { key: "name", header: "Connection", label: "Connection", render: (c) => (<><strong>{c.name}</strong> <span className="badge neutral">{c.type.toUpperCase()}</span></>) },
            { key: "state", header: "Status", label: "Status", render: (c) => (c.enabled ? <span className="badge ok">enabled</span> : <span className="badge neutral">disabled</span>) },
            { key: "idp", header: "Identity provider", label: "Identity provider", render: (c) => <span className="mono small">{c.type === "saml" ? c.idpEntityId : c.issuer}</span> },
            { key: "role", header: "Default role", label: "Default role", render: (c) => ROLE_LABELS[c.defaultRole] },
            {
              key: "act",
              header: "",
              align: "right",
              render: (c) => (
                <RowMenu label={`Actions for ${c.name}`}>
                  <button type="button" role="menuitem" onClick={() => setEditing({ id: c.id, existing: c, form: connectionForm(c) })}>Edit</button>
                  <button type="button" role="menuitem" className="danger" onClick={() => setDeleting(c)}>Delete</button>
                </RowMenu>
              ),
            },
          ]}
        />
      </div>

      <div className="card form-card" style={{ marginTop: 16 }}>
        <section className="section">
          <h2>Sign-in policy</h2>
          <p className="muted small">Every member signs in with a second factor. That is not configurable.</p>
          <div className="settings-stack">
            <Switch
              checked={requireSso}
              onChange={setRequireSso}
              label="Require single sign-on"
              hint={hasEnabledConnection ? "Local passwords stop working, except for break-glass owners." : "Enable a connection above before you can require single sign-on."}
              id="require-sso"
            />
            {requireSso && (
              <Field
                label="Break-glass owners"
                hint="Owners who may still sign in locally with a passkey plus authenticator code. Every break-glass login raises an alert."
                error={missingBreakGlass ? "Pick at least one break-glass owner, or you can lock everyone out." : undefined}
              >
                {(p) => (
                  <select {...p} className="input" multiple value={breakGlass} onChange={(e) => setBreakGlass(Array.from(e.target.selectedOptions).map((o) => o.value))} style={{ height: 96, maxWidth: 420 }}>
                    {breakGlassEligible.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.email}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
          </div>
        </section>
      </div>
      <SaveBar
        dirty={enforcementDirty}
        saving={saveEnforcement.isPending}
        disabled={missingBreakGlass || (requireSso && !hasEnabledConnection)}
        onSave={() => saveEnforcement.mutate()}
        onDiscard={() => {
          if (!org.data) return;
          setRequireSso(org.data.settings.requireSso);
          setBreakGlass(org.data.settings.breakGlassUserIds);
        }}
        label="the sign-in policy"
      />

      <Dialog open={!!editing} onClose={closeEditing} title={`${editing?.id ? "Edit" : "Add"} ${f?.type.toUpperCase()} connection`} width={640} footer={<><button className="btn" onClick={closeEditing}>Cancel</button><button className="btn primary" onClick={() => save.mutate()} disabled={save.isPending || !f?.name || (!!editing?.id && !connectionDirty)}>Save</button></>}>
        {f && (
          <>
            <UnsavedGuard dirty={connectionDirty} />
            <Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={(e) => setF({ name: e.target.value })} />}</Field>
            <Switch checked={f.enabled} onChange={(v) => setF({ enabled: v })} label="Enabled" />
            <hr style={{ border: 0, borderTop: "1px solid var(--border)", margin: "16px 0" }} />
            {editing?.existing && (
              <section style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 13, margin: "0 0 8px" }}>Give these to your identity provider</h3>
                <dl className="kv-inline">
                  {f.type === "saml" ? (
                    <>
                      <dt>SP entity ID</dt><dd className="mono small">{editing.existing.spEntityId} <CopyButton value={editing.existing.spEntityId ?? ""} /></dd>
                      <dt>ACS URL</dt><dd className="mono small">{editing.existing.spAcsUrl} <CopyButton value={editing.existing.spAcsUrl ?? ""} /></dd>
                      <dt>SP metadata</dt><dd className="mono small">{editing.existing.spMetadataUrl}</dd>
                    </>
                  ) : (
                    <>
                      <dt>Redirect URI</dt><dd className="mono small">{editing.existing.redirectUri} <CopyButton value={editing.existing.redirectUri ?? ""} /></dd>
                    </>
                  )}
                </dl>
              </section>
            )}
            {!editing?.existing && <p className="muted small">Save once to get the SP entity ID, ACS URL, and redirect URI to paste into your identity provider.</p>}
            {f.type === "saml" ? (
              <>
                <Field label="IdP entity ID">{(p) => <input {...p} className="input" value={f.idpEntityId ?? ""} onChange={(e) => setF({ idpEntityId: e.target.value })} />}</Field>
                <Field label="IdP SSO URL">{(p) => <input {...p} className="input" value={f.idpSsoUrl ?? ""} onChange={(e) => setF({ idpSsoUrl: e.target.value })} />}</Field>
                <Field label="IdP signing certificate" hint="PEM format.">{(p) => <textarea {...p} className="input" value={f.idpCertificate ?? ""} onChange={(e) => setF({ idpCertificate: e.target.value })} />}</Field>
              </>
            ) : (
              <>
                <Field label="Issuer URL" hint="Discovery is read from {issuer}/.well-known/openid-configuration.">{(p) => <input {...p} className="input" value={f.issuer ?? ""} onChange={(e) => setF({ issuer: e.target.value })} />}</Field>
                <Field label="Client ID">{(p) => <input {...p} className="input" value={f.clientId ?? ""} onChange={(e) => setF({ clientId: e.target.value })} />}</Field>
                <Field label="Client secret" hint={editing?.existing?.clientSecretSet ? "A secret is stored. Leave blank to keep it." : undefined}>
                  {(p) => <input {...p} className="input" type="password" value={f.clientSecret ?? ""} onChange={(e) => setF({ clientSecret: e.target.value })} autoComplete="off" />}
                </Field>
              </>
            )}
            <hr style={{ border: 0, borderTop: "1px solid var(--border)", margin: "16px 0" }} />
            <Switch checked={f.jitProvisioning} onChange={(v) => setF({ jitProvisioning: v })} label="Create members on first sign-in" hint="Just-in-time provisioning." />
            <Field label="Default role">
              {(p) => (
                <select {...p} className="input" value={f.defaultRole} onChange={(e) => setF({ defaultRole: e.target.value as Role })} style={{ maxWidth: 240 }} title={ROLE_SUMMARY[f.defaultRole]}>
                  {SSO_ROLES.map((r) => (
                    <option key={r} value={r} title={ROLE_SUMMARY[r]}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Group → role mapping" hint="Group names from the SAML attribute `groups` or the OIDC claim `groups`. First match wins; otherwise the default role applies.">
              {(p) => (
                <div id={p.id}>
                  {Object.entries(f.groupRoleMap).map(([g, r]) => (
                    <div key={g} className="actions-row" style={{ marginBottom: 6 }}>
                      <span className="mono small">{g}</span> → <span>{ROLE_LABELS[r]}</span>
                      <button className="btn ghost sm" onClick={() => { const m = { ...f.groupRoleMap }; delete m[g]; setF({ groupRoleMap: m }); }} aria-label={`Remove mapping ${g}`}>✕</button>
                    </div>
                  ))}
                  <div className="actions-row">
                    <input className="input" placeholder="group name" value={groupKey} onChange={(e) => setGroupKey(e.target.value)} style={{ maxWidth: 200 }} aria-label="Group name" autoComplete="off" />
                    <select className="input" value={groupRole} onChange={(e) => setGroupRole(e.target.value as Role)} style={{ maxWidth: 160 }} aria-label="Mapped role" autoComplete="off" title={ROLE_SUMMARY[groupRole]}>
                      {SSO_ROLES.map((r) => (
                        <option key={r} value={r} title={ROLE_SUMMARY[r]}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                    <button className="btn sm" type="button" onClick={() => { if (!groupKey.trim()) return; setF({ groupRoleMap: { ...f.groupRoleMap, [groupKey.trim()]: groupRole } }); setGroupKey(""); }}>Add</button>
                  </div>
                </div>
              )}
            </Field>
          </>
        )}
      </Dialog>
      <ConfirmDialog
        open={discardOpen}
        title="Discard changes"
        confirmLabel="Discard"
        onClose={() => setDiscardOpen(false)}
        onConfirm={() => {
          setDiscardOpen(false);
          setEditing(null);
        }}
      >
        <p style={{ margin: 0 }}>Unsaved connection changes are discarded.</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={!!deleting}
        danger
        title="Delete connection"
        confirmLabel="Delete connection"
        busy={del.isPending}
        onClose={() => { if (!del.isPending) setDeleting(null); }}
        onConfirm={() => deleting && del.mutate(deleting.id)}
      >
        <p style={{ margin: 0 }}><strong>{deleting?.name}</strong> stops accepting sign-ins. Members who used it sign in another way.</p>
      </ConfirmDialog>
    </OrgLayout>
  );
}
